import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import {
  BIENVENIDA_SUPER_EJECUTIVO,
  NOMBRE_SUPER_EJECUTIVO,
  PIXELES_CERCA_DEL_FINAL_SUPER_EJECUTIVO,
  CARACTERES_CERCA_DEL_LIMITE_SUPER_EJECUTIVO,
  MAXIMO_CARACTERES_SUPER_EJECUTIVO,
  MENSAJES_SUPER_EJECUTIVO,
  PREGUNTAS_EJEMPLO_SUPER_EJECUTIVO,
} from '../super-ejecutivo.constants';
import { IAreaPanelSuperEjecutivo, TamanoPanelSuperEjecutivo } from '../super-ejecutivo.interface';
import { SuperEjecutivoService } from '../super-ejecutivo.service';
import { SuperEjecutivoMarkdownComponent } from '../super-ejecutivo-markdown/super-ejecutivo-markdown.component';
import { SuperEjecutivoPropuestaComponent } from '../super-ejecutivo-propuesta/super-ejecutivo-propuesta.component';

/** Panel de conversación con el Super Ejecutivo (RN4-RN17). */
@Component({
  selector: 'app-super-ejecutivo-panel',
  templateUrl: './super-ejecutivo-panel.component.html',
  styleUrl: './super-ejecutivo-panel.component.css',
  imports: [
    CdkTextareaAutosize,
    MatButton,
    MatIconButton,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatProgressSpinner,
    MatTooltip,
    SuperEjecutivoMarkdownComponent,
    SuperEjecutivoPropuestaComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperEjecutivoPanelComponent {
  public readonly superEjecutivo = inject(SuperEjecutivoService);
  private readonly injector = inject(Injector);

  /** Tamaño del panel (RN5, RN5.1). */
  public readonly tamano = input.required<TamanoPanelSuperEjecutivo>();
  /** Área central de Kobra Admin, para el panel agrandado (RN5). */
  public readonly area = input<IAreaPanelSuperEjecutivo | null>(null);

  private readonly listaMensajes = viewChild<ElementRef<HTMLElement>>('listaMensajes');
  /** Mensajes que había la última vez: uno nuevo siempre se lleva a la vista. */
  private totalMensajes = 0;

  public readonly nombre = NOMBRE_SUPER_EJECUTIVO;
  public readonly bienvenida = BIENVENIDA_SUPER_EJECUTIVO;
  public readonly preguntasEjemplo = PREGUNTAS_EJEMPLO_SUPER_EJECUTIVO;
  public readonly maximoCaracteres = MAXIMO_CARACTERES_SUPER_EJECUTIVO;
  public readonly cercaDelLimite = CARACTERES_CERCA_DEL_LIMITE_SUPER_EJECUTIVO;
  public readonly mensajeMuyLargo = MENSAJES_SUPER_EJECUTIVO.MENSAJE_MUY_LARGO;

  /** Posición del panel agrandado sobre el área central; los otros tamaños van por clase. */
  public readonly estiloArea = computed(() => {
    const area = this.area();

    if (this.tamano() !== 'agrandado' || !area) {
      return null;
    }

    return { top: `${area.top}px`, left: `${area.left}px`, width: `${area.width}px`, height: `${area.height}px` };
  });

  public readonly largo = computed(() => this.superEjecutivo.borrador().length);
  public readonly muyLargo = computed(() => this.largo() > MAXIMO_CARACTERES_SUPER_EJECUTIVO);
  public readonly puedeEnviar = computed(
    () => !this.superEjecutivo.procesando() && this.superEjecutivo.borrador().trim() !== '' && !this.muyLargo(),
  );
  public readonly conversacionVacia = computed(() => this.superEjecutivo.mensajes().length === 0 && !this.superEjecutivo.procesando());

  constructor() {
    // Lo nuevo a la vista: baja al final con un mensaje nuevo, o con texto, avance o error si el usuario ya
    // estaba al final. Si subió a leer mientras llega una respuesta larga, no lo mueve.
    effect(() => {
      const total = this.superEjecutivo.mensajes().length;
      this.superEjecutivo.avance();
      this.superEjecutivo.error();
      const mensajeNuevo = total > this.totalMensajes;
      this.totalMensajes = total;

      if (mensajeNuevo || this.estaCercaDelFinal()) {
        afterNextRender(() => this.irAlFinal(), { injector: this.injector });
      }
    });
  }

  public enviar(): void {
    if (this.puedeEnviar()) {
      void this.superEjecutivo.enviarMensaje(this.superEjecutivo.borrador());
    }
  }

  public preguntar(pregunta: string): void {
    void this.superEjecutivo.enviarMensaje(pregunta);
  }

  public alEscribir(evento: Event): void {
    this.superEjecutivo.borrador.set((evento.target as HTMLTextAreaElement).value);
  }

  /** Enter envía; Shift+Enter hace un salto de línea. */
  public alPresionarEnter(evento: Event): void {
    if ((evento as KeyboardEvent).shiftKey) {
      return;
    }

    evento.preventDefault();
    this.enviar();
  }

  public decidir(approvalId: string, aprobada: boolean): void {
    void this.superEjecutivo.decidirPropuesta(approvalId, aprobada);
  }

  private estaCercaDelFinal(): boolean {
    const lista = this.listaMensajes()?.nativeElement;

    return !lista || lista.scrollHeight - lista.scrollTop - lista.clientHeight < PIXELES_CERCA_DEL_FINAL_SUPER_EJECUTIVO;
  }

  private irAlFinal(): void {
    const lista = this.listaMensajes()?.nativeElement;

    if (lista) {
      lista.scrollTop = lista.scrollHeight;
    }
  }
}
